// Was ein gescannter Code bedeutet.
//
// Der Scanner liefert eine Zeichenkette — mehr weiß er nicht. Ob darin
// eine Bestellung steckt, eine ISBN oder der Speisekarten-QR vom Italiener
// nebenan, entscheidet diese Datei. Sie ist bewusst rein: kein Fetch, kein
// Router, kein DOM. Deshalb ist sie ohne Kamera prüfbar, und die
// Fallunterscheidung steht an einer Stelle statt verteilt im Overlay.
//
// Drei Ausgänge, mehr gibt es nicht:
//   nummer — Kandidat für eine Bestellung; die tolerante Auflösung im
//            Dashboard entscheidet, ob daraus ein Sprung wird
//   suche  — ins Suchfeld und suchen (ISBN vom Buchrücken, alles Übrige)
//   fremd  — kein Tracking-Code. Wird angezeigt, nie geöffnet.
//
// Der letzte Punkt ist der wichtigste: eine fremde URL wird NIE
// aufgerufen. Ein QR-Code ist ein Zettel, den jeder beschriften kann; wer
// ihn auf ein Buch klebt, dürfte sonst bestimmen, wohin das Tablet der
// Buchhandlung navigiert.

export type ScanDeutung =
  | { art: 'nummer'; wert: string }
  | { art: 'suche'; wert: string }
  | { art: 'fremd' };

/** Unsere eigene Nummer, echt wie geübt. */
const BESTELLNR = /^[BÜ]-\d{4}-\d{4}$/u;

/** Kurze Ziffernfolge: Nummernkürzel oder Shop-Bestellnummer. */
const ZIFFERN_KURZ = /^\d{1,6}$/;

/** EAN-13 mit Buchland-Präfix — das ist eine ISBN vom Buchrücken. */
const ISBN_EAN = /^97[89]\d{10}$/;

/** Irgendein Schema vornedran (http:, mailto:, tel:, wifi:, javascript:). */
const MIT_SCHEMA = /^[a-z][a-z0-9+.-]*:/i;

/**
 * Adresse ohne Schema — "www.beispiel.de" oder "beispiel.de/xy".
 *
 * Solche QR-Codes gibt es reichlich. Ohne diese Erkennung landete der Text
 * im Suchfeld und der Mitarbeiter bekäme "keine Treffer" statt einer
 * Antwort auf das, was er wirklich gescannt hat.
 */
const OHNE_SCHEMA_URL = /^(www\.\S+|[a-z0-9-]+(\.[a-z0-9-]+)+\/\S*)$/i;

/**
 * Wessen QR-Codes von uns stammen (#39, A28).
 *
 * HIER STAND EINE LISTE MIT ZWEI FESTEN DOMAINS.
 *   'tracking.buch-engel.com' und 'tracking-dev.buch-engel.com'. Sie war
 *   die einzige Stelle im ausgelieferten Code, an der ein Domain-Umzug
 *   etwas zerbrochen hätte — und zwar STUMM: Nach dem Umzug wäre jeder
 *   gescannte Bestellzettel „fremd“ gewesen. Keine Meldung, kein Sprung,
 *   nichts. Zwei neue feste Werte einzutragen hätte das Problem nur um
 *   ein paar Jahre verschoben.
 *
 * DER EIGENE HOST GILT IMMER.
 *   `window.location.host` ist per Definition „wir“ — von dieser Adresse
 *   kommt die laufende Anwendung. Damit zieht der Scanner beim Umzug von
 *   selbst mit, ohne dass jemand etwas einträgt, und in der Entwicklung
 *   (localhost) gilt er ohne Sonderregel mit.
 *
 * ZUSATZ-HOSTS KOMMEN AUS DEN EINSTELLUNGEN.
 *   Gebraucht werden sie für die Übergangszeit: Die BEREITS GEDRUCKTEN
 *   Zettel tragen die ALTE Domain, und die liegen noch monatelang im
 *   Laden. Der Wert steht in `settings.scan_hosts` (siehe
 *   DEPLOY-NOTES.md, „Umzug auf eine andere Domain“).
 *
 * VERGLICHEN WIRD EXAKT, nicht auf Endung. Ein Suffix-Vergleich ließe
 * `tracking.buch-engel.com.example.net` als unseren durchgehen — eine
 * Domain, die sich jeder registrieren kann.
 */
export function eigeneHosts(zusatz: readonly string[] = []): string[] {
  const hier = typeof window === 'undefined' ? '' : window.location.host.toLowerCase();
  const alle = hier === '' ? [...zusatz] : [hier, ...zusatz];
  // Doppelte raus: der eigene Host kann auch in den Einstellungen stehen,
  // etwa direkt nach dem Umzug, wenn beide Adressen gepflegt sind.
  return [...new Set(alle.map((h) => h.trim().toLowerCase()).filter((h) => h !== ''))];
}

/**
 * Die Zusatz-Hosts aus dem Einstellungs-Wert lesen.
 *
 * Erwartet eine Liste, getrennt durch Komma, Leerzeichen oder Zeilenende:
 *   "tracking-alt.buch-engel.com, tracking-dev.buch-engel.com"
 *
 * Nachsichtig beim Lesen, streng beim Vergleichen: Wer aus der
 * Adresszeile kopiert, bringt „https://“ und einen Schrägstrich mit. Das
 * hier zu schlucken kostet vier Zeilen; es nicht zu schlucken kostet
 * einen stillen Fehlschlag, den niemand findet — der Wert sieht ja
 * richtig aus.
 *
 * Was danach übrig bleibt, wird EXAKT verglichen (siehe eigeneHosts).
 */
export function hostsAusEinstellung(wert: string | null | undefined): string[] {
  if (typeof wert !== 'string') return [];
  return wert
    .split(/[\s,;]+/)
    .map((teil) => teil.trim().toLowerCase())
    .filter((teil) => teil !== '')
    // Schema weg ("https://host/pfad" -> "host/pfad"), dann Pfad weg.
    .map((teil) => teil.replace(/^[a-z][a-z0-9+.-]*:\/\//, '').split('/')[0])
    .filter((teil) => teil !== '');
}

/** Nackter Inhalt ohne Adresse drumherum. */
function nacktDeuten(wert: string): ScanDeutung {
  const t = wert.trim();
  if (t === '') return { art: 'fremd' };
  if (BESTELLNR.test(t)) return { art: 'nummer', wert: t };
  if (ZIFFERN_KURZ.test(t)) return { art: 'nummer', wert: t };
  // ISBN und alles Weitere gehen denselben Weg: ins Suchfeld. Die ISBN
  // steht trotzdem eigens da, weil sie der gemeinte Fall ist — der
  // Auspacktisch sucht die offenen Bestellungen zum Buch in der Hand.
  if (ISBN_EAN.test(t)) return { art: 'suche', wert: t };
  if (OHNE_SCHEMA_URL.test(t)) return { art: 'fremd' };
  return { art: 'suche', wert: t };
}

/** Der Pfad-Anteil unserer eigenen Adressen. */
function pfadDeuten(pfad: string): ScanDeutung {
  // Leerer letzter Abschnitt (Schrägstrich am Ende) fällt weg, damit
  // "/s/B-2026-0001/" genauso trägt wie ohne Schrägstrich.
  const teile = pfad.split('/').filter((s) => s !== '');
  if (teile.length >= 2 && teile[0] === 's') {
    return nacktDeuten(entschluesseln(teile[1]));
  }
  // /bestellungen/{nr} und /bestellungen/{nr}/druck
  if (teile.length >= 2 && teile[0] === 'bestellungen') {
    return nacktDeuten(entschluesseln(teile[1]));
  }
  return { art: 'fremd' };
}

/** decodeURIComponent, ohne bei kaputter Prozent-Folge zu werfen. */
function entschluesseln(s: string): string {
  try {
    return decodeURIComponent(s);
  } catch {
    return s;
  }
}

/**
 * Was tun mit dem, was die Kamera gelesen hat?
 *
 * @param roh     der Text aus dem Code, unverändert
 * @param hosts   unsere eigenen Hosts (Voreinstellung: eigeneHosts())
 */
export function scanDeuten(roh: string, hosts: readonly string[] = eigeneHosts()): ScanDeutung {
  const t = roh.trim();
  if (t === '') return { art: 'fremd' };

  if (MIT_SCHEMA.test(t)) {
    let url: URL;
    try {
      url = new URL(t);
    } catch {
      return { art: 'fremd' };
    }
    // Alles außer http/https ist von vornherein nichts für uns —
    // mailto:, tel:, wifi:, und erst recht javascript:.
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return { art: 'fremd' };
    const host = url.host.toLowerCase();
    if (!hosts.some((h) => h.toLowerCase() === host)) return { art: 'fremd' };
    return pfadDeuten(url.pathname);
  }

  return nacktDeuten(t);
}
